import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useWishlist } from '../contexts/WishlistContext';
import { Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const WishlistPage = ({ onAddToCart }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { wishlist, removeFromWishlist } = useWishlist();

  const handleAddToCart = (product) => {
    onAddToCart(product);
    toast({
      title: t('cart.add'),
      description: `${product.name} ${t('products.add_to_cart')}`,
    });
  };

  const handleRemove = (productId) => {
    removeFromWishlist(productId);
    toast({
      title: t('common.success'),
      description: t('wishlist.remove_from_wishlist'),
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">{t('wishlist.title')}</h1>
          <p className="text-gray-600">{wishlist.length} {t('cart.items')}</p>
        </div>

        {wishlist.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {wishlist.map((product) => (
              <Card key={product.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                <div className="relative">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-48 object-cover cursor-pointer"
                    onClick={() => navigate(`/product/${product.id}`)}
                  />
                  <button
                    onClick={() => handleRemove(product.id)}
                    className="absolute top-2 right-2 bg-white rounded-full p-2 shadow-md hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 text-red-600" />
                  </button>
                </div>

                <div className="p-4">
                  <p className="text-sm font-semibold text-blue-600 mb-1">{product.brand}</p>
                  <h3
                    className="font-semibold text-gray-900 mb-2 line-clamp-2 cursor-pointer hover:text-blue-600"
                    onClick={() => navigate(`/product/${product.id}`)}
                  >
                    {product.name}
                  </h3>
                  <p className="text-2xl font-bold text-gray-900 mb-4">
                    OMR {product.price.toFixed(2)}
                  </p>
                  <Button
                    onClick={() => handleAddToCart(product)}
                    className="w-full bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700"
                  >
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    {t('products.add_to_cart')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="p-12 text-center">
            <Heart className="h-16 w-16 mx-auto text-gray-400 mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('wishlist.empty')}</h3>
            <p className="text-gray-600 mb-6">
              {t('wishlist.empty_description')}
            </p>
            <Button onClick={() => navigate('/products')}>
              {t('wishlist.start_shopping')}
            </Button>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
